import React, { useState, useEffect } from 'react'
import { clickableProps } from '../utils/clickableProps'

/**
 * Year shown on the card. Festivals that straddle New Year get both ends
 * ("2025–2026") rather than a year that contradicts half the event.
 */
const getEventYear = (event) => {
  const start = event.startDate ? new Date(event.startDate) : null
  if (!start || isNaN(start.getTime())) return null

  const startYear = start.getFullYear()
  const end = event.endDate ? new Date(event.endDate) : null
  const endYear = end && !isNaN(end.getTime()) ? end.getFullYear() : startYear

  return endYear !== startYear ? `${startYear}–${endYear}` : String(startYear)
}

const EventCard = ({
  event,
  isOwnPassport,
  onEventClick,
  onDeleteEvent,
  onOpenPhotoSelector,
  getEventImage,
  getAllEventPhotos
}) => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                     window.innerWidth < 768
      setIsMobile(mobile)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const year = getEventYear(event)

  // Get border color based on privacy setting
  const getBorderColor = () => {
    switch (event.privacy) {
      case 'public':
        return 'border-green-500/60'
      case 'friends':
        return 'border-blue-500/60'
      case 'private':
        return 'border-purple-500/60'
      default:
        return 'border-white/20'
    }
  }

  return (
    <div
      className={`group relative bg-white/10 ${isMobile ? '' : 'backdrop-blur-xl'} rounded-3xl shadow-2xl border-2 ${getBorderColor()} overflow-hidden hover:scale-105 hover:shadow-3xl transition-all duration-300 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400`}
      {...clickableProps(
        () => onEventClick(event._id),
        `${event.name}${year ? `, ${year}` : ''}`
      )}
      style={{
        aspectRatio: '3/4',
        minHeight: '240px'
      }}
    >
      {/* Event Image */}
      <div className="relative h-3/5 bg-gradient-to-br from-purple-600/20 to-pink-600/20">
        {getEventImage(event) ? (
          <img
            src={getEventImage(event)}
            alt={event.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
            </svg>
          </div>
        )}

        {/* Year badge — a passport is browsed by "which year was that",
            so the year sits on the image where it reads at a glance rather
            than competing for space in the cramped text block below. */}
        {year && (
          <div className="absolute top-2 left-2 bg-black/70 backdrop-blur-sm text-white text-xs font-semibold px-2 py-1 rounded-lg border border-white/20">
            {year}
          </div>
        )}

        {/* Delete Button - Only for own passport */}
        {isOwnPassport && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteEvent(event._id, event.name);
            }}
            className="absolute top-2 right-2 bg-red-600/80 hover:bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all duration-300"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Event Info */}
      <div className="relative h-2/5 p-3 flex flex-col justify-between">
        <div className="flex-1">
          <h3 className="text-sm font-semibold mb-1 line-clamp-2" style={{color: 'var(--theme-text-primary)'}}>{event.name}</h3>
          <p className="text-xs mb-1 line-clamp-1" style={{color: 'var(--theme-text-secondary)'}}>{event.venueName}</p>
          <p className="text-xs line-clamp-1" style={{color: 'var(--theme-text-muted)'}}>{event.country}</p>
        </div>

        {/* Bottom Right Controls */}
        <div className="flex items-center justify-end gap-2 mt-2">
          {/* Photo Selector Button - Only for own passport */}
          {isOwnPassport && getAllEventPhotos(event).length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onOpenPhotoSelector(event._id);
              }}
              className="bg-blue-600/80 hover:bg-blue-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all duration-300 transform hover:scale-110"
              title="Change default photo"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
          )}

          {/* Stamp Indicator */}
          {event.hasStamp && (
            <div className="bg-green-600/90 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 transform hover:scale-105 transition-transform">
              📸 Stamped
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventCard